import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { haptics } from '../telegram.js'

/**
 * Round 36×36 chrome button used for header actions: close, back, toggle.
 *
 * One source of truth for the round button shape so close/back/eye-toggle
 * across screens all look like the same control. Previously some used
 * an ASCII × in a round bg-brand-surface button while others used an
 * inline SVG arrow inside an identical round container — same shape,
 * different optical weight, different stroke widths.
 *
 * Variants:
 *  - `default`  — neutral surface, muted icon. Used for back/close.
 *  - `accent`   — filled with brand-primary-strong. Used when the toggle
 *                 is "on" (e.g. text reveal active).
 */

export type IconButtonVariant = 'default' | 'accent'

interface IconButtonProps
  extends Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    'className' | 'children'
  > {
  variant?: IconButtonVariant
  /** SVG icon — sized to w-4 h-4 by the component. */
  children: ReactNode
  /** Required for screen readers since these are icon-only. */
  'aria-label': string
  /** Optional title (tooltip). */
  title?: string
  haptic?: boolean
  className?: string
}

const VARIANT_CLASSES: Record<IconButtonVariant, string> = {
  default: 'bg-brand-surface text-brand-text-muted border border-brand-border',
  accent:
    'bg-brand-primary-strong text-brand-on-primary border border-brand-primary-strong',
}

export function IconButton({
  variant = 'default',
  haptic = true,
  onClick,
  children,
  className = '',
  ...rest
}: IconButtonProps) {
  return (
    <button
      onClick={(e) => {
        if (haptic) haptics.light()
        onClick?.(e)
      }}
      className={`w-9 h-9 rounded-full flex items-center justify-center active:scale-95 transition-transform ${VARIANT_CLASSES[variant]} ${className}`}
      {...rest}
    >
      <span className="w-4 h-4 flex items-center justify-center">
        {children}
      </span>
    </button>
  )
}
